import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  lazy,
  Suspense,
} from "react";
import { createPortal } from "react-dom";
import styles from "./CodeBlock.module.css";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import ZoomableWrapper from "../ZoomableWrapper/ZoomableWrapper";
import { parseDiagramJson, jsonToMermaid } from "./MermaidJsonConverter";
import ExportMenu from "../ImageExportMenu/ImageExportMenu";
import CopyImageButton from "../CopyImageButton/CopyImageButton";

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

interface CodeBlockProps {
  children: string;
  className?: string;
  props?: Record<string, any>;
  backgroundColor?: string;
  isStreaming?: boolean;
}

type SortDirection = "asc" | "desc" | null;
interface SortState {
  column: string;
  direction: SortDirection;
}
interface ParsedCsv {
  headers: string[];
  rows: string[][];
  error?: string;
}

// ─────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────

const LIGHT_GRAY = "#F6F8FA";
const CSV_PAGE_SIZE = 50;

// draw.io is intentionally excluded — requires external viewer.diagrams.net
// which is unavailable in air-gapped environments. It is download-only.
const PREVIEWABLE_LANGUAGES = new Set([
  "html",
  "svg",
  "xml",
  "mermaid",
  "bpmn",
  "csv",
  "tsv",
  "vega-lite",
  "vegalite",
  "vega",
]);

const FILE_EXTENSIONS: Record<string, string> = {
  html: "html",
  svg: "svg",
  xml: "xml",
  mermaid: "mmd",
  bpmn: "bpmn",
  drawio: "drawio",
  csv: "csv",
  tsv: "tsv",
  "vega-lite": "json",
  vegalite: "json",
  javascript: "js",
  typescript: "ts",
  python: "py",
  java: "java",
  c: "c",
  cpp: "cpp",
  csharp: "cs",
  css: "css",
  json: "json",
  markdown: "md",
  sql: "sql",
  bash: "sh",
  shell: "sh",
  go: "go",
  rust: "rs",
  php: "php",
  ruby: "rb",
  swift: "swift",
  kotlin: "kt",
  text: "txt",
};

const STABILITY_DELAY_MS = 400;
const MIN_IFRAME_HEIGHT = 100;
const MAX_IFRAME_HEIGHT = 3000;

// ─────────────────────────────────────────────────────────────
// Lazy imports
// ─────────────────────────────────────────────────────────────

const SyntaxHighlighter = lazy(() =>
  import("react-syntax-highlighter").then((module) => ({
    default: module.Prism,
  })),
);

// ─────────────────────────────────────────────────────────────
// Mermaid — initialise once at module level
// Calling mermaid.initialize() on every render caused internal
// state resets and parser inconsistencies — fixed by using a
// module-level singleton flag.
// ─────────────────────────────────────────────────────────────

let mermaidInitialized = false;

async function getMermaid() {
  const mermaid = (await import("mermaid")).default;
  if (!mermaidInitialized) {
    mermaid.initialize({
      startOnLoad: false,
      theme: "base",
      themeVariables: {
        primaryColor: "#ffffff",
        primaryTextColor: "#24242a",
        primaryBorderColor: "#50585f",
        lineColor: "#50585f",
        secondaryColor: "#e0e1df",
        tertiaryColor: "#f6f8fa",
        fontFamily: "system-ui, -apple-system, sans-serif",
      },
      securityLevel: "loose",
      flowchart: { htmlLabels: false },
      sequence: { showSequenceNumbers: true },
    });
    mermaidInitialized = true;
  }
  return mermaid;
}

/**
 * Auto-quote any node label containing characters that trip up the
 * Chevrotain-based flowchart/graph parser still used in Mermaid v11.
 * Even though @mermaid-js/parser v0.6.3 (Langium) is installed, flowchart
 * diagrams still go through the old Chevrotain path.
 *
 *   NodeId[label with (parens) or / slashes, commas]
 * → NodeId["label with (parens) or / slashes, commas"]
 *
 * Already-quoted labels (NodeId["..."]) are left untouched.
 */
function sanitizeMermaidCode(code: string): string {
  return code.replace(
    /(\b[\w-]+)\[([^\]"]*[()\/,][^\]"]*)\]/g,
    (_match, id, label) => `${id}["${label.replace(/"/g, "'")}"]`,
  );
}

// ─────────────────────────────────────────────────────────────
// CSV helpers
// ─────────────────────────────────────────────────────────────

async function parseCsv(code: string, delimiter: string): Promise<ParsedCsv> {
  const Papa = (await import("papaparse")).default;
  const result = Papa.parse<string[]>(code.trim(), {
    delimiter,
    skipEmptyLines: true,
  });
  if (result.errors.length && !result.data.length) {
    return { headers: [], rows: [], error: result.errors[0].message };
  }
  const [headers, ...rows] = result.data as string[][];
  return { headers: headers ?? [], rows };
}

// ─────────────────────────────────────────────────────────────
// CSV Table component
// Rendered directly in React — no iframe needed.
// Supports sort (click column header, cycles asc → desc → off),
// global text filter, and pagination.
// ─────────────────────────────────────────────────────────────

const CsvTablePreview: React.FC<{ code: string; delimiter?: string }> = ({
  code,
  delimiter = ",",
}) => {
  const [parsed, setParsed] = useState<ParsedCsv | null>(null);
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState<SortState>({ column: "", direction: null });
  const [page, setPage] = useState(0);

  useEffect(() => {
    parseCsv(code, delimiter).then(setParsed);
  }, [code, delimiter]);

  const filtered = useMemo(() => {
    if (!parsed) return [];
    const q = filter.toLowerCase();
    return q
      ? parsed.rows.filter((row) =>
          row.some((cell) => cell.toLowerCase().includes(q)),
        )
      : parsed.rows;
  }, [parsed, filter]);

  const sorted = useMemo(() => {
    if (!sort.column || !sort.direction || !parsed) return filtered;
    const idx = parsed.headers.indexOf(sort.column);
    if (idx === -1) return filtered;
    return [...filtered].sort((a, b) => {
      const av = a[idx] ?? "";
      const bv = b[idx] ?? "";
      const nA = parseFloat(av);
      const nB = parseFloat(bv);
      const cmp = !isNaN(nA) && !isNaN(nB) ? nA - nB : av.localeCompare(bv);
      return sort.direction === "asc" ? cmp : -cmp;
    });
  }, [filtered, sort, parsed]);

  const totalPages = Math.ceil(sorted.length / CSV_PAGE_SIZE);
  const pageRows = sorted.slice(
    page * CSV_PAGE_SIZE,
    (page + 1) * CSV_PAGE_SIZE,
  );

  const handleSort = (col: string) => {
    setSort((prev) => ({
      column: col,
      direction:
        prev.column === col
          ? prev.direction === "asc"
            ? "desc"
            : prev.direction === "desc"
              ? null
              : "asc"
          : "asc",
    }));
    setPage(0);
  };

  const sortIcon = (col: string) => {
    if (sort.column !== col || !sort.direction) return " ⇅";
    return sort.direction === "asc" ? " ↑" : " ↓";
  };

  if (!parsed)
    return (
      <div className={styles.codePreviewLoading}>
        <div className={styles.codePreviewSpinner} />
        <span>Parsing…</span>
      </div>
    );

  if (parsed.error)
    return <div className={styles.codePreviewError}>{parsed.error}</div>;

  return (
    <div
      style={{
        padding: "12px",
        fontFamily: "system-ui, sans-serif",
        fontSize: "13px",
      }}
    >
      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "10px",
          gap: "8px",
          flexWrap: "wrap",
        }}
      >
        <input
          type="text"
          placeholder="Filter rows…"
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value);
            setPage(0);
          }}
          style={{
            padding: "5px 10px",
            border: "1px solid #d1d5db",
            borderRadius: "6px",
            fontSize: "13px",
            outline: "none",
            minWidth: "200px",
            flex: 1,
          }}
        />
        <span style={{ color: "#6b7280", whiteSpace: "nowrap" }}>
          {sorted.length.toLocaleString()} row
          {sorted.length !== 1 ? "s" : ""}
          {filter
            ? ` (filtered from ${parsed.rows.length.toLocaleString()})`
            : ""}
        </span>
      </div>

      {/* Table */}
      <div
        style={{
          overflowX: "auto",
          borderRadius: "8px",
          border: "1px solid #e5e7eb",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            minWidth: "400px",
            tableLayout: "fixed",
          }}
        >
          <thead>
            <tr style={{ background: "#f9fafb" }}>
              {parsed.headers.map((h) => (
                <th
                  key={h}
                  onClick={() => handleSort(h)}
                  style={{
                    padding: "10px 12px",
                    textAlign: "left",
                    fontWeight: 600,
                    fontSize: "12px",
                    color: "#374151",
                    cursor: "pointer",
                    userSelect: "none",
                    borderBottom: "2px solid #e5e7eb",
                    whiteSpace: "pre-line",
                    wordBreak: "break-word",
                    verticalAlign: "bottom",
                    lineHeight: "1.4",
                    background: sort.column === h ? "#f0f4ff" : undefined,
                  }}
                >
                  {h}
                  <span style={{ color: "#9ca3af", marginLeft: "2px" }}>
                    {sortIcon(h)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td
                  colSpan={parsed.headers.length}
                  style={{
                    padding: "24px",
                    textAlign: "center",
                    color: "#9ca3af",
                  }}
                >
                  No matching rows
                </td>
              </tr>
            ) : (
              pageRows.map((row, ri) => (
                <tr
                  key={ri}
                  style={{
                    background: ri % 2 === 0 ? "#fff" : "#f9fafb",
                    borderBottom: "1px solid #f3f4f6",
                  }}
                >
                  {parsed.headers.map((_, ci) => (
                    <td
                      key={ci}
                      style={{
                        padding: "10px 12px",
                        borderBottom: "1px solid #f3f4f6",
                        color: "#374151",
                        maxWidth: "400px",
                        minWidth: "120px",
                        whiteSpace: "pre-line",
                        wordBreak: "break-word",
                        verticalAlign: "top",
                        lineHeight: "1.45",
                      }}
                    >
                      {row[ci] ?? ""}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginTop: "10px",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            style={{
              padding: "4px 10px",
              borderRadius: "5px",
              border: "1px solid #d1d5db",
              background: page === 0 ? "#f3f4f6" : "#fff",
              cursor: page === 0 ? "default" : "pointer",
              fontSize: "12px",
              color: page === 0 ? "#9ca3af" : "#374151",
            }}
          >
            ← Prev
          </button>
          <span style={{ fontSize: "12px", color: "#6b7280" }}>
            Page {page + 1} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page === totalPages - 1}
            style={{
              padding: "4px 10px",
              borderRadius: "5px",
              border: "1px solid #d1d5db",
              background: page === totalPages - 1 ? "#f3f4f6" : "#fff",
              cursor: page === totalPages - 1 ? "default" : "pointer",
              fontSize: "12px",
              color: page === totalPages - 1 ? "#9ca3af" : "#374151",
            }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Vega-Lite hook + component
// Rendered directly in React — no iframe needed.
// vega-embed is a pure npm package, fully offline.
// ─────────────────────────────────────────────────────────────

function useVegaLiteRender(
  code: string,
  containerRef: React.RefObject<HTMLDivElement | null>,
  enabled: boolean,
) {
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!enabled || !containerRef.current) return;
    let cancelled = false;
    setReady(false);
    setError("");

    (async () => {
      try {
        let spec: any;
        try {
          spec = JSON.parse(code);
        } catch {
          throw new Error("Invalid JSON in Vega-Lite spec");
        }

        // ── Apply layout defaults for readable charts ──────
        if (!spec.padding) {
          spec.padding = { left: 20, right: 20, top: 10, bottom: 10 };
        }
        if (!spec.config) spec.config = {};
        const cfg = spec.config;

        // Axis defaults — spacing, font sizes, label padding
        if (!cfg.axis) cfg.axis = {};
        if (!cfg.axis.labelFontSize) cfg.axis.labelFontSize = 12;
        if (!cfg.axis.titleFontSize) cfg.axis.titleFontSize = 13;
        if (!cfg.axis.titlePadding) cfg.axis.titlePadding = 12;
        if (!cfg.axis.labelPadding) cfg.axis.labelPadding = 8;

        // X-axis specific — avoid overlapping labels
        if (!cfg.axisX) cfg.axisX = {};
        if (cfg.axisX.labelAngle === undefined) cfg.axisX.labelAngle = 0;

        // Title defaults
        if (!cfg.title) cfg.title = {};
        if (!cfg.title.fontSize) cfg.title.fontSize = 16;
        if (!cfg.title.offset) cfg.title.offset = 12;
        if (!cfg.title.anchor) cfg.title.anchor = "start";

        // Consistent bar sizing
        if (!cfg.bar) cfg.bar = {};
        if (!cfg.bar.discreteBandSize) cfg.bar.discreteBandSize = { band: 0.7 };

        // Chart dimensions if not specified
        if (!spec.width) spec.width = "container";
        if (!spec.height && !spec.encoding?.row && !spec.encoding?.column) {
          spec.height = 300;
        }
        // ── End layout defaults ────────────────────────────

        const vegaEmbed = (await import("vega-embed")).default;
        if (cancelled || !containerRef.current) return;

        await vegaEmbed(containerRef.current, spec, {
          actions: {
            export: true,
            source: false,
            compiled: false,
            editor: false,
          },
          renderer: "svg",
          theme: "latimes",
        });

        if (!cancelled) setReady(true);
      } catch (err: any) {
        if (!cancelled)
          setError(err?.message ?? "Failed to render Vega-Lite chart");
      }
    })();

    return () => {
      cancelled = true;
      if (containerRef.current) containerRef.current.innerHTML = "";
    };
  }, [code, enabled]);

  return { error, ready };
}

const VegaLitePreview: React.FC<{ code: string; enabled: boolean }> = ({
  code,
  enabled,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Don't attempt render until JSON looks complete
  const trimmed = code.trim();
  const isCompleteJson = trimmed.startsWith("{") && trimmed.endsWith("}");

  const { error, ready } = useVegaLiteRender(
    code,
    containerRef,
    enabled && isCompleteJson,
  );

  return (
    <div style={{ padding: "12px" }}>
      {!ready && !error && (
        <div className={styles.codePreviewLoading}>
          <div className={styles.codePreviewSpinner} />
          <span>Rendering chart…</span>
        </div>
      )}
      {error && <div className={styles.codePreviewError}>{error}</div>}
      <div
        ref={containerRef}
        style={{ width: "100%", visibility: ready ? "visible" : "hidden" }}
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// iframe resize helpers
// ─────────────────────────────────────────────────────────────

function buildResizeScript(frameId: string): string {
  return `<script>
(function() {
  var frameId = "${frameId}";
  var wrapper = document.getElementById('measure-layer');
  var target = wrapper || document.body;
  var lastHeight = 0;
  var timer = null;

  function report() {
    var h = target.getBoundingClientRect().height;
    h = Math.ceil(h) + 4;
    if (Math.abs(h - lastHeight) > 4) {
      lastHeight = h;
      window.parent.postMessage({ type: "iframe-resize", frameId: frameId, height: h }, "*");
    }
  }

  var ro = new ResizeObserver(function() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(report, 20);
  });

  ro.observe(target);
  window.addEventListener('load', report);
  setTimeout(report, 500);
})();
<\/script>`;
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function buildSrcdoc(language: string, code: string, frameId: string): string {
  const resizeScript = buildResizeScript(frameId);

  const baseStyle = `
    <style>
      html, body {
        margin: 0; padding: 0; background: #fff;
        height: auto !important; min-height: 0 !important;
        overflow: hidden;
      }
      #measure-layer {
        padding: 16px;
        box-sizing: border-box;
        display: block;
        width: 100%;
        overflow: auto;
        font-family: system-ui, -apple-system, sans-serif;
      }
      img, svg { max-width: 100%; height: auto; display: block; }
    </style>
  `;

  const svgStyle = `
    <style>
      html, body {
        margin: 0; padding: 0; background: #fff;
        height: auto !important;
        overflow: visible !important;
      }
      #measure-layer {
        padding: 16px;
        box-sizing: border-box;
        display: block;
        width: 100%;
      }
      #measure-layer svg {
        width: 100%;
        height: auto;
        max-width: 100%;
        display: block;
      }
    </style>
  `;

  const wrap = (content: string, isSvg = false) =>
    `<!DOCTYPE html><html><head>${isSvg ? svgStyle : baseStyle}</head><body><div id="measure-layer">${content}</div>${resizeScript}</body></html>`;

  switch (language) {
    case "svg":
      return wrap(code, true);
    case "xml":
      return wrap(
        `<pre style="margin:0; white-space: pre-wrap; word-wrap: break-word;">${escapeHtml(code)}</pre>`,
      );
    case "html":
    default:
      if (code.includes("<body")) {
        const loopFix = `<style>html,body{height:auto !important; min-height:0 !important; overflow:hidden !important;}</style>`;
        return code
          .replace("</head>", `${loopFix}</head>`)
          .replace("</body>", `${resizeScript}</body>`);
      }
      return wrap(code);
  }
}

function buildSvgSrcdoc(svgContent: string, frameId: string): string {
  return buildSrcdoc("svg", svgContent, frameId);
}

// ─────────────────────────────────────────────────
// Custom hooks
// ─────────────────────────────────────────────────────────────

function useContentStable(children: string, isStreaming: boolean): string {
  // Instead of a boolean, return the last "committed" version of the content.
  // This means the preview only updates when content has settled —
  // it never blanks out mid-stream.
  const [committedContent, setCommittedContent] = useState(
    isStreaming ? "" : children,
  );
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!isStreaming) {
      setCommittedContent(children);
      return;
    }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(
      () => setCommittedContent(children),
      STABILITY_DELAY_MS,
    );
    return () => clearTimeout(timerRef.current);
  }, [children, isStreaming]);

  return committedContent;
}

function useIframeAutoHeight(
  frameId: string,
  iframeRef: React.RefObject<HTMLIFrameElement | null>,
  resetKey: any,
): number | null {
  const [height, setHeight] = useState<number | null>(null);
  const currentHeightRef = useRef<number | null>(null);

  useEffect(() => {
    setHeight(null);
    currentHeightRef.current = null;
  }, [resetKey]);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (
        event.data?.type === "iframe-resize" &&
        event.data.frameId === frameId &&
        typeof event.data.height === "number"
      ) {
        const newHeight = event.data.height;
        const current = currentHeightRef.current ?? 0;
        if (Math.abs(newHeight - current) < 4) return;
        const clamped = Math.min(
          Math.max(newHeight, MIN_IFRAME_HEIGHT),
          MAX_IFRAME_HEIGHT,
        );
        currentHeightRef.current = clamped;
        setHeight(clamped);
      }
    }
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [frameId]);

  return height;
}

function useMermaidRender(code: string, enabled: boolean) {
  const [svg, setSvg] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [rendering, setRendering] = useState(false);
  const idRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const currentId = `mermaid-render-${Date.now()}-${idRef.current++}`;
    setRendering(true);
    // NOTE: intentionally NOT clearing svg here — keep showing last good render

    (async () => {
      try {
        const mermaid = await getMermaid();
        const sanitized = sanitizeMermaidCode(code);
        const { svg: rendered } = await mermaid.render(currentId, sanitized);
        if (!cancelled) {
          setSvg(rendered);
          setError("");
        }
      } catch (err: any) {
        if (!cancelled) {
          setSvg("");
          setError(err?.message ?? "Failed to render Mermaid diagram");
        }
        document.getElementById(currentId)?.remove();
      } finally {
        if (!cancelled) setRendering(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [code, enabled]);

  return { svg, error, rendering };
}

function useBpmnSrcdoc(code: string, frameId: string, enabled: boolean) {
  const [srcdoc, setSrcdoc] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const { buildBpmnSrcdoc } = await import("./bpmnSrcdocBuilder");
        const html = await buildBpmnSrcdoc(code, frameId);
        if (!cancelled) {
          setSrcdoc(html);
          setError("");
        }
      } catch (err: any) {
        if (!cancelled) {
          setSrcdoc("");
          setError(err?.message ?? "Failed to build BPMN preview");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [code, frameId, enabled]);

  return { srcdoc, error, loading };
}

// ─────────────────────────────────────────────────────────────
// Frame ID counter
// ─────────────────────────────────────────────────────────────

let globalFrameCounter = 0;

// ─────────────────────────────────────────────────────────────
// Icon components
// ─────────────────────────────────────────────────────────────

const IconCode = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </svg>
);

const IconEye = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const IconExpand = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 3 21 3 21 9" />
    <polyline points="9 21 3 21 3 15" />
    <line x1="21" y1="3" x2="14" y2="10" />
    <line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);

const IconImage = ({ size = 16 }: { size?: number }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

const IconCheck = ({ size = 16 }: { size?: number }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const IconDownload = ({ size = 16 }: { size?: number }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const IconCopy = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
  </svg>
);

const IconClose = () => (
  <svg
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// ─────────────────────────────────────────────────────────────
// CodeBlock component
// ─────────────────────────────────────────────────────────────

const CodeBlock: React.FC<CodeBlockProps> = React.memo(
  ({
    children,
    className,
    backgroundColor = LIGHT_GRAY,
    isStreaming = false,
  }) => {
    const [copied, setCopied] = useState(false);
    const [imageCopied, setImageCopied] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    // ── IDs & refs ───────────────────────────────────────────
    const inlineIframeRef = useRef<HTMLIFrameElement>(null);
    const modalIframeRef = useRef<HTMLIFrameElement>(null);
    const frameIdRef = useRef(`cb-frame-${Date.now()}-${globalFrameCounter++}`);
    const frameId = frameIdRef.current;
    const modalFrameId = `${frameId}-modal`;

    // ── Language detection ───────────────────────────────────
    const match = useMemo(
      () => /language-([\w-]+)/.exec(className || "") || [null, "text"],
      [className],
    );

    const language = (match[1] ?? "text").toLowerCase();
    const isPreviewable = PREVIEWABLE_LANGUAGES.has(language);
    const isMermaid = language === "mermaid";
    const isBpmn = language === "bpmn";
    const isSvg = language === "svg";
    const isHtml = language === "html";
    const isCsv = language === "csv";
    const isTsv = language === "tsv";
    const isVegaLite =
      language === "vega-lite" ||
      language === "vegalite" ||
      language === "vega";

    const [showPreview, setShowPreview] = useState(isPreviewable);
    const committedContent = useContentStable(children, isStreaming);
    const isBlockReady = committedContent.length > 0;

    // ── Mermaid JSON → mermaid code conversion ───────────────
    // If the LLM produced diagram JSON inside a ```mermaid fence,
    // convert it to real mermaid syntax before rendering.
    // ── Mermaid JSON → mermaid code conversion ───────────────
    const resolvedMermaidCode = useMemo(() => {
      if (!isMermaid) return committedContent;

      const trimmed = committedContent.trim();
      if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) {
        // Incomplete JSON — return empty to suppress mermaid render attempts
        if (trimmed.startsWith("{")) return "";
        return committedContent;
      }

      const diagramData = parseDiagramJson(committedContent);
      if (diagramData) {
        const { code, error } = jsonToMermaid(diagramData);
        if (!error) return code;
        console.warn("Diagram JSON conversion failed:", error);
      }
      return committedContent;
    }, [committedContent, isMermaid]);

    // Track whether JSON conversion was used (for "show code" toggle)
    const wasJsonConverted = useMemo(() => {
      if (!isMermaid) return false;
      return (
        parseDiagramJson(committedContent) !== null &&
        resolvedMermaidCode !== committedContent
      );
    }, [committedContent, resolvedMermaidCode, isMermaid]);

    // ── Mermaid ──────────────────────────────────────────────
    const {
      svg: mermaidSvg,
      error: mermaidError,
      rendering: mermaidRendering,
    } = useMermaidRender(
      resolvedMermaidCode,
      isMermaid &&
        showPreview &&
        isBlockReady &&
        resolvedMermaidCode.length > 0,
    );

    // ── BPMN ─────────────────────────────────────────────────
    const activeFrameId = isExpanded ? modalFrameId : frameId;
    const {
      srcdoc: bpmnSrcdoc,
      error: bpmnError,
      loading: bpmnLoading,
    } = useBpmnSrcdoc(
      children,
      activeFrameId,
      isBpmn && showPreview && isBlockReady,
    );

    // ── srcdoc & auto-height ─────────────────────────────────
    // CSV, TSV, and Vega-Lite render directly in React — no srcdoc needed.
    const [iframeMountKey, setIframeMountKey] = useState(0);
    const prevSrcdocRef = useRef<string>("");

    const inlineHeight = useIframeAutoHeight(
      frameId,
      inlineIframeRef,
      iframeMountKey,
    );
    const modalHeight = useIframeAutoHeight(
      modalFrameId,
      modalIframeRef,
      iframeMountKey,
    );

    const inlineSrcdoc = useMemo(() => {
      if (!showPreview || !isPreviewable || !isBlockReady) return "";
      if (isBpmn) return bpmnSrcdoc;
      if (isMermaid && mermaidSvg) return buildSvgSrcdoc(mermaidSvg, frameId);
      if (isMermaid || isCsv || isTsv || isVegaLite) return "";
      return buildSrcdoc(language, children, frameId);
    }, [
      showPreview,
      isPreviewable,
      isBlockReady,
      isBpmn,
      bpmnSrcdoc,
      isMermaid,
      mermaidSvg,
      isCsv,
      isTsv,
      isVegaLite,
      language,
      children,
      frameId,
    ]);

    useEffect(() => {
      if (inlineSrcdoc && inlineSrcdoc !== prevSrcdocRef.current) {
        prevSrcdocRef.current = inlineSrcdoc;
        setIframeMountKey((k) => k + 1);
      }
    }, [inlineSrcdoc]);

    const modalSrcdoc = useMemo(() => {
      if (!isExpanded) return "";
      if (isBpmn) return bpmnSrcdoc;
      if (isMermaid && mermaidSvg)
        return buildSvgSrcdoc(mermaidSvg, modalFrameId);
      if (isMermaid || isCsv || isTsv || isVegaLite) return "";
      return buildSrcdoc(language, children, modalFrameId);
    }, [
      isExpanded,
      isBpmn,
      bpmnSrcdoc,
      isMermaid,
      mermaidSvg,
      isCsv,
      isTsv,
      isVegaLite,
      language,
      children,
      modalFrameId,
    ]);

    // ── Keyboard shortcut ────────────────────────────────────
    useEffect(() => {
      const handleEsc = (e: KeyboardEvent) => {
        if (e.key === "Escape") setIsExpanded(false);
      };
      if (isExpanded) window.addEventListener("keydown", handleEsc);
      return () => window.removeEventListener("keydown", handleEsc);
    }, [isExpanded]);

    // ── Handlers ─────────────────────────────────────────────
    const handleCopy = useCallback(async () => {
      try {
        // When JSON was converted, copy the generated mermaid code (more useful)
        const textToCopy = wasJsonConverted ? resolvedMermaidCode : children;
        await navigator.clipboard.writeText(textToCopy);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy text:", err);
      }
    }, [children, wasJsonConverted, resolvedMermaidCode]);
    // ── Render helpers ────────────────────────────────────────

    const renderPreviewContent = (isModal: boolean) => {
      if (!isBlockReady)
        return (
          <div className={styles.codePreviewLoading}>
            <div className={styles.codePreviewSpinner} />
            <span>Processing…</span>
          </div>
        );

      // CSV / TSV
      if (isCsv || isTsv)
        return (
          <CsvTablePreview code={children} delimiter={isTsv ? "\t" : ","} />
        );

      // Vega-Lite
      if (isVegaLite)
        return (
          <ZoomableWrapper enabled={true}>
            <VegaLitePreview
              code={committedContent}
              enabled={showPreview && isBlockReady}
            />
          </ZoomableWrapper>
        );

      // Mermaid — error display
      if (isMermaid && mermaidError)
        return <div className={styles.codePreviewError}>{mermaidError}</div>;

      // Mermaid — render SVG directly in React (no iframe)
      if (isMermaid && mermaidSvg) {
        return (
          <ZoomableWrapper enabled={true}>
            <div
              style={{
                padding: "16px",
                width: "100%",
                boxSizing: "border-box",
                overflow: "auto",
                background: "#fff",
              }}
              dangerouslySetInnerHTML={{ __html: mermaidSvg }}
            />
          </ZoomableWrapper>
        );
      }

      if (isMermaid && !mermaidSvg && !mermaidRendering)
        return (
          <div className={styles.codePreviewLoading}>
            <span>Rendering diagram…</span>
          </div>
        );

      // BPMN errors & loading
      if (isBpmn && bpmnError)
        return <div className={styles.codePreviewError}>{bpmnError}</div>;
      if (isBpmn && (bpmnLoading || !bpmnSrcdoc))
        return (
          <div className={styles.codePreviewLoading}>
            <span>Loading BPMN viewer…</span>
          </div>
        );

      // All other types via iframe — allow scrolling for HTML
      const srcdoc = isModal ? modalSrcdoc : inlineSrcdoc;
      const allowScroll = isModal || isSvg || isHtml;
      const style: React.CSSProperties = isModal
        ? { width: "100%", height: "100%", border: "none" }
        : {
            height: inlineHeight ? `${inlineHeight}px` : "400px",
            minHeight: "200px",
          };

      const iframeEl = (
        <iframe
          key={isModal ? "modal" : `inline-${iframeMountKey}`}
          ref={isModal ? modalIframeRef : inlineIframeRef}
          srcDoc={srcdoc}
          sandbox="allow-scripts allow-same-origin"
          title={`${language} preview`}
          className={styles.codePreviewIframe}
          style={{
            ...style,
            overflow: allowScroll ? "auto" : "hidden",
          }}
        />
      );

      // Enable zoom for modal view (or inline SVG/BPMN)
      if (isModal || isSvg || isBpmn) {
        return <ZoomableWrapper enabled={true}>{iframeEl}</ZoomableWrapper>;
      }

      return iframeEl;
    };

    const getModalStyle = (): React.CSSProperties => {
      if (isBpmn) return { width: "90vw", height: "85vh" };
      if (isCsv || isTsv) return { width: "min(1400px, 95vw)", height: "80vh" };
      if (isVegaLite) return { width: "min(1000px, 90vw)", height: "auto" };
      if (isMermaid)
        return { width: "min(1200px, 95vw)", height: "85vh", overflow: "auto" };
      const calcHeight = modalHeight ? modalHeight + 80 : null;
      return {
        width: "min(1200px, 90vw)",
        height: calcHeight ? `${calcHeight}px` : "70vh",
      };
    };

    // ── Code to display in "show code" view ──────────────────
    // When JSON was converted, show the generated mermaid code
    // instead of the raw JSON — more useful for the user.
    const displayCode =
      isMermaid && wasJsonConverted ? resolvedMermaidCode : children;

    // ── Render ────────────────────────────────────────────────

    return (
      <>
        <div className={styles.codeBlockContainer}>
          <div className={styles.codeBlockHeader}>
            <div className={styles.languageLabel}>
              <span className={styles.labelSpan}>{language}</span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              {/* Toggle preview / code */}
              {isPreviewable && (
                <>
                  <button
                    onClick={() => setShowPreview((v) => !v)}
                    className={styles.codeBlockCopyButton}
                    title={showPreview ? "Show code" : "Show preview"}
                  >
                    {showPreview ? <IconCode /> : <IconEye />}
                  </button>
                  {showPreview && (
                    <button
                      onClick={() => setIsExpanded(true)}
                      className={styles.codeBlockCopyButton}
                      title="Expand preview"
                    >
                      <IconExpand />
                    </button>
                  )}
                </>
              )}

              {/* Export menu — replaces download + copy-as-image */}
              <ExportMenu
                iframeRef={inlineIframeRef}
                code={displayCode}
                language={language}
                svgContent={isMermaid ? mermaidSvg : undefined}
              />
              {(isHtml || isVegaLite || isMermaid) && (
                <CopyImageButton
                  iframeRef={inlineIframeRef}
                  language={language}
                  svgContent={isMermaid ? mermaidSvg : undefined}
                />
              )}

              {/* Copy source */}
              <button
                onClick={handleCopy}
                className={`${styles.codeBlockCopyButton} ${copied ? styles.copied : ""}`}
                title="Copy code"
              >
                {copied ? <IconCheck /> : <IconCopy />}
              </button>
            </div>
          </div>

          {showPreview && isPreviewable ? (
            <div className={styles.codePreviewWrapper}>
              {renderPreviewContent(false)}
            </div>
          ) : (
            <div className={styles.codeContentWrapper}>
              <Suspense
                fallback={
                  <div className={styles.loadingPlaceholder}>Loading…</div>
                }
              >
                <SyntaxHighlighter
                  language={language}
                  style={oneLight}
                  wrapLines
                  customStyle={{
                    margin: 0,
                    padding: "16px",
                    backgroundColor: "transparent",
                    fontSize: "13px",
                    lineHeight: "1.6",
                  }}
                >
                  {displayCode}
                </SyntaxHighlighter>
              </Suspense>
            </div>
          )}
        </div>

        {/* Expand modal */}
        {isExpanded &&
          isPreviewable &&
          showPreview &&
          createPortal(
            <div
              className={styles.modalOverlay}
              onClick={() => setIsExpanded(false)}
            >
              <div
                className={styles.modalContent}
                onClick={(e) => e.stopPropagation()}
                style={getModalStyle()}
              >
                <div className={styles.modalHeader}>
                  <span className={styles.modalTitle}>{language} Preview</span>
                  <div style={{ display: "flex", gap: "8px" }}>
                    {/* Export menu in modal too */}
                    <ExportMenu
                      iframeRef={modalIframeRef}
                      code={displayCode}
                      language={language}
                      svgContent={isMermaid ? mermaidSvg : undefined}
                    />
                    {(isHtml || isVegaLite || isMermaid) && (
                      <CopyImageButton
                        iframeRef={modalIframeRef}
                        language={language}
                        svgContent={isMermaid ? mermaidSvg : undefined}
                      />
                    )}
                    <button
                      className={styles.modalCloseBtn}
                      onClick={() => setIsExpanded(false)}
                    >
                      <IconClose />
                    </button>
                  </div>
                </div>
                <div className={styles.modalBody}>
                  {renderPreviewContent(true)}
                </div>
              </div>
            </div>,
            document.body,
          )}
      </>
    );
  },
);

export default CodeBlock;
